import { useState } from "react";
import { ArrowDown, ArrowUp, Check, DotsSixVertical, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Pill } from "@/components/admin/AdminUI";

export const labelError = (t) => {
  const n = t.trim().length;
  return n < 1 || n > 40 ? "Use 1–40 characters." : null;
};

function InlineEdit({ it, onSave, onCancel }) {
  const [text, setText] = useState(it.label);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const invalid = labelError(text);
  const save = async () => {
    if (invalid || text.trim() === it.label) return onCancel();
    setBusy(true);
    const err = await onSave(text.trim());
    setBusy(false);
    if (err) setError(err); else onCancel();
  };
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <label htmlFor={`int-edit-${it.id}`} className="sr-only">Interest label</label>
        <Input id={`int-edit-${it.id}`} autoFocus value={text} maxLength={40} onChange={(e) => { setText(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
          className="h-9 max-w-sm rounded-lg" aria-invalid={!!(invalid || error)} data-testid={`interest-edit-input-${it.id}`} />
        <Button size="icon" variant="ghost" onClick={save} disabled={busy || !!invalid} aria-label="Save label" data-testid={`interest-edit-save-${it.id}`}><Check /></Button>
        <Button size="icon" variant="ghost" onClick={onCancel} aria-label="Cancel" data-testid={`interest-edit-cancel-${it.id}`}><X /></Button>
      </div>
      {(invalid || error) && <p className="cw-error mt-1 text-xs" role="alert" data-testid={`interest-edit-error-${it.id}`}>{error || invalid}</p>}
    </div>
  );
}

export default function InterestRow({ it, index, total, dragging, onMove, onPatch, onDelete, drag }) {
  const [editing, setEditing] = useState(false);
  const used = it.usage_count || 0;
  return (
    <li draggable={!editing} onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; drag.start(); }} onDragEnd={drag.end}
      onDragOver={(e) => { e.preventDefault(); drag.over(); }}
      className={`flex items-center gap-3 px-3 py-4 transition-colors cw-ease sm:px-5 ${dragging ? "cw-bg-tint opacity-70" : "bg-white"}`} data-testid={`interest-row-${it.id}`}>
      <span className="cw-muted hidden cursor-grab active:cursor-grabbing sm:block" aria-hidden><DotsSixVertical size={20} /></span>
      <span className="cw-muted w-6 text-right text-xs tabular-nums" data-testid={`interest-position-${it.id}`}>{index + 1}</span>
      {editing ? <InlineEdit it={it} onSave={(label) => onPatch({ label })} onCancel={() => setEditing(false)} /> : (
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => setEditing(true)} className={`rounded text-left text-sm font-medium hover:underline ${it.active ? "" : "cw-muted"}`} data-testid={`interest-label-${it.id}`}>{it.label}</button>
          <p className="cw-muted mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span data-testid={`interest-usage-${it.id}`}>used by {used} profile{used === 1 ? "" : "s"}</span>{!it.active && <Pill>Off</Pill>}
          </p>
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" disabled={index === 0} onClick={() => onMove(index - 1)} aria-label={`Move ${it.label} up`} data-testid={`interest-up-button-${it.id}`}><ArrowUp /></Button>
        <Button size="icon" variant="ghost" disabled={index === total - 1} onClick={() => onMove(index + 1)} aria-label={`Move ${it.label} down`} data-testid={`interest-down-button-${it.id}`}><ArrowDown /></Button>
        {!editing && <Button size="icon" variant="ghost" onClick={() => setEditing(true)} aria-label={`Edit ${it.label}`} data-testid={`interest-edit-button-${it.id}`}><PencilSimple /></Button>}
        {used === 0
          ? <Button size="icon" variant="ghost" className="cw-error" onClick={onDelete} aria-label={`Delete ${it.label}`} data-testid={`interest-delete-button-${it.id}`}><Trash /></Button>
          : it.active
            ? <Button size="sm" variant="ghost" onClick={() => onPatch({ active: false })} title="In use, so it can't be deleted" data-testid={`interest-deactivate-button-${it.id}`}>Deactivate</Button>
            : <span className="w-[88px]" aria-hidden />}
        <Switch checked={!!it.active} onCheckedChange={(v) => onPatch({ active: v })} aria-label={`${it.active ? "Turn off" : "Turn on"} ${it.label}`} className="ml-2" data-testid={`interest-toggle-${it.id}`} />
      </div>
    </li>
  );
}
